import React, { useState } from 'react';
import { cezaArttir, cezaEksilt } from '../utils/cezaHesaplamalari';
import { PlusIcon, MinusIcon, TrashIcon, CalculatorIcon, ChevronDownIcon, ArrowRightIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, ArrowUturnLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Sayı input'larındaki tarayıcı artırma/azaltma oklarını gizler
const noSpinner =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

const formatCezaWithSpaces = (ceza) => {
  const parts = [];
  if (ceza.year > 0) parts.push(`${ceza.year} yıl`);
  if (ceza.month > 0) parts.push(`${ceza.month} ay`);
  if (ceza.day > 0) parts.push(`${ceza.day} gün`);
  return parts.join(' ') + ' '; // Sona bir boşluk ekliyoruz
};

const formatAPC = (fine) => {
  return fine > 0 ? `${fine} gün APC` : '';
};

// Hiç ceza girilmemiş mi? (tüm alanlar 0)
const isCezaBos = (c) => !c.year && !c.month && !c.day && !c.fine;

const Calculation = ({ onChange, data }) => {
  const [ceza, setCeza] = useState({ year: 0, month: 0, day: 0, fine: 0 });
  const [oran, setOran] = useState({ numerator: 1, denominator: 2 });
  const [hesaplamaGecmisi, setHesaplamaGecmisi] = useState([]);
  const [customNumerator, setCustomNumerator] = useState('');
  const [customDenominator, setCustomDenominator] = useState('');
  const [isCustomRate, setIsCustomRate] = useState(false);

  const handleCezaChange = (e) => {
    const { name, value } = e.target;
    const digits = value.replace(/\D/g, ''); // yalnızca rakamları al
    setCeza(prev => ({ ...prev, [name]: digits === '' ? 0 : parseInt(digits, 10) }));
  };

  const handleOranChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'other') {
      setIsCustomRate(true);
      // Diğer seçeneği seçildiğinde mevcut oranı koruyalım
      setCustomNumerator(oran.numerator.toString());
      setCustomDenominator(oran.denominator.toString());
    } else {
      setIsCustomRate(false);
      const [numerator, denominator] = selectedValue.split('/').map(Number);
      setOran({ numerator, denominator });
      setCustomNumerator(numerator.toString());
      setCustomDenominator(denominator.toString());
    }
  };

  const handleCustomRateChange = (part) => (e) => {
    const value = e.target.value;
    if (part === 'numerator') {
      setCustomNumerator(value);
    } else {
      setCustomDenominator(value);
    }
    
    const numerator = part === 'numerator' ? parseInt(value, 10) : parseInt(customNumerator, 10);
    const denominator = part === 'denominator' ? parseInt(value, 10) : parseInt(customDenominator, 10);
    
    if (numerator && denominator) {
      setOran({ numerator, denominator });
    }
  };

  const hesapla = (isArttir) => {
    if (isCezaBos(ceza)) return;
    let yeniCeza;
    if (isArttir) {
      yeniCeza = cezaArttir(ceza, { rate: oran });
    } else {
      yeniCeza = cezaEksilt(ceza, { rate: oran });
    }
    // Yeni hesaplamayı listenin sonuna ekliyoruz
    setHesaplamaGecmisi(prev => [...prev, { ceza, oran, sonuc: yeniCeza, isArttir }]);
    setCeza(yeniCeza);
    if (onChange) onChange(yeniCeza);
  };

  const yeniHesap = () => {
    setCeza({ year: 0, month: 0, day: 0, fine: 0 });
    setOran({ numerator: 1, denominator: 2 });
    setCustomNumerator('');
    setCustomDenominator('');
    setIsCustomRate(false);
    setHesaplamaGecmisi([]);
  };

  const handleYukariTasi = (gecmisCeza, e) => {
    e.stopPropagation(); // Tıklama olayının üst öğelere yayılmasını engeller
    setCeza(gecmisCeza);
  };

  const handleGecmisSil = (index, e) => {
    e.stopPropagation(); // Tıklama olayının üst öğelere yayılmasını engeller
    setHesaplamaGecmisi(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputFocus = (event) => {
    event.target.select();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="app-card mb-8 animate-fade-in-up">
            <div className="app-header">
              <div className="relative z-10 flex items-center">
                <div className="app-header-icon">
                  <CalculatorIcon className="w-5 h-5" />
                </div>
                <h1 className="app-header-title ml-3">
                  Ceza Hesaplama
                </h1>
              </div>
            </div>
            <div className="bg-gradient-to-b from-gray-50 to-white p-6">
              <div className="mb-8">
                <div className="flex justify-end">
                  <button
                    onClick={yeniHesap}
                    className="btn-secondary w-full md:w-auto"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Yeni Hesap</span>
                  </button>
                </div>
              </div>
              <div className="space-y-8">
                {/* Yıl, Ay, Gün Grid */}
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    {['Yıl', 'Ay', 'Gün'].map((label, index) => {
                      const key = ['year', 'month', 'day'][index];
                      return (
                        <div key={index} className="group relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            name={key}
                            value={ceza[key] || ''}
                            onChange={handleCezaChange}
                            onFocus={handleInputFocus}
                            placeholder="0"
                            className="peer w-full rounded-xl border border-slate-300 bg-white px-4 pb-3 pt-7 text-center text-2xl font-semibold text-slate-800 shadow-sm outline-none transition-all duration-200 placeholder:font-normal placeholder:text-slate-300 hover:border-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25"
                          />
                          <span className="pointer-events-none absolute inset-x-0 top-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 transition-colors duration-200 peer-focus:text-slate-600">
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      name="fine"
                      value={ceza.fine || ''}
                      onChange={handleCezaChange}
                      onFocus={handleInputFocus}
                      placeholder="0"
                      className="peer w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-14 text-lg font-medium text-slate-800 shadow-sm outline-none transition-all duration-200 placeholder:text-slate-300 hover:border-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25"
                    />
                    <label className="pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1.5 text-xs font-medium text-slate-500 transition-colors duration-200 peer-focus:text-slate-700">Adli Para Cezası</label>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">gün</span>
                  </div>
                </div>

                {/* Oran ve Butonlar Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                  <div className="relative flex items-center space-x-2">
                    <label className="pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1.5 text-xs font-medium text-slate-500">Oran</label>
                    <div className={`relative ${isCustomRate ? 'w-1/3' : 'w-full'}`}>
                      <select
                        value={isCustomRate ? 'other' : `${oran.numerator}/${oran.denominator}`}
                        onChange={handleOranChange}
                        className="w-full h-12 pl-4 pr-10 appearance-none bg-white border border-slate-300 rounded-xl shadow-sm outline-none transition-all duration-200 text-base text-slate-800 hover:border-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25"
                      >
                        {['1/1','1/2', '1/3','1/4', '1/6', '2/3', '3/4', '5/6', '1/12'].map(rate => (
                          <option key={rate} value={rate}>{rate}</option>
                        ))}
                        <option value="other">Diğer</option>
                      </select>
                      <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    </div>
                    {isCustomRate && (
                      <>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={customNumerator}
                          onChange={handleCustomRateChange('numerator')}
                          onFocus={handleInputFocus}
                          onWheel={(e) => e.target.blur()}
                          className={`w-1/3 h-12 px-4 text-center border border-slate-300 rounded-xl shadow-sm outline-none transition-all duration-200 text-base text-slate-800 hover:border-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25 ${noSpinner}`}
                          placeholder="Pay"
                        />
                        <span className="text-2xl font-bold text-slate-400">/</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={customDenominator}
                          onChange={handleCustomRateChange('denominator')}
                          onFocus={handleInputFocus}
                          onWheel={(e) => e.target.blur()}
                          className={`w-1/3 h-12 px-4 text-center border border-slate-300 rounded-xl shadow-sm outline-none transition-all duration-200 text-base text-slate-800 hover:border-slate-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25 ${noSpinner}`}
                          placeholder="Payda"
                        />
                      </>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => hesapla(true)}
                      disabled={isCezaBos(ceza)}
                      className="btn-success group flex-1"
                    >
                      <PlusIcon className="h-5 w-5 transition-transform group-hover:scale-110" />
                      Arttır
                    </button>
                    <button
                      onClick={() => hesapla(false)}
                      disabled={isCezaBos(ceza)}
                      className="btn-danger group flex-1"
                    >
                      <MinusIcon className="h-5 w-5 transition-transform group-hover:scale-110" />
                      Azalt
                    </button>
                  </div>
                </div>

                {/* Sonuç ceza kartı */}
                {hesaplamaGecmisi.length > 0 && (
                  <div className="app-panel-dark mt-8">
                    <div className="relative z-10">
                      <div className="text-sm text-slate-200 mb-2">
                        {formatCezaWithSpaces(hesaplamaGecmisi[hesaplamaGecmisi.length - 1].ceza)}
                        {hesaplamaGecmisi[hesaplamaGecmisi.length - 1].ceza.fine > 0 &&
                          <span className="block text-xs text-slate-400">
                            {formatAPC(hesaplamaGecmisi[hesaplamaGecmisi.length - 1].ceza.fine)}
                          </span>
                        }
                        ceza {' '}
                        {hesaplamaGecmisi[hesaplamaGecmisi.length - 1].oran.numerator}/
                        {hesaplamaGecmisi[hesaplamaGecmisi.length - 1].oran.denominator} oranında
                        {hesaplamaGecmisi[hesaplamaGecmisi.length - 1].isArttir ? ' arttırıldı' : ' eksiltildi'} ve bulunan sonuç:
                      </div>
                      <div className="text-3xl font-bold text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.25)]">
                        {formatCezaWithSpaces(ceza)}
                        {ceza.fine > 0 &&
                          <span className="block text-lg font-semibold text-amber-300 mt-1">
                            {formatAPC(ceza.fine)}
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                )}

                {/* Boş durum */}
                {hesaplamaGecmisi.length === 0 && (
                  <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-10 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                      <CalculatorIcon className="h-6 w-6" />
                    </div>
                    <p className="mt-3 text-sm font-medium text-slate-500">Henüz hesaplama yapılmadı</p>
                    <p className="mt-1 text-xs text-slate-400">
                      Bir ceza süresi girip <span className="font-semibold text-emerald-600">Arttır</span> veya <span className="font-semibold text-rose-600">Azalt</span>'a basın.
                    </p>
                  </div>
                )}

                {/* Hesaplama Geçmişi */}
                {hesaplamaGecmisi.length > 0 && (
                  <div className="mt-8">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-800">Hesaplama Geçmişi</h3>
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">{hesaplamaGecmisi.length} adım</span>
                    </div>
                    <div className="relative space-y-3">
                      <div className="pointer-events-none absolute bottom-3 left-[18px] top-3 w-px bg-slate-200"></div>
                      {hesaplamaGecmisi.map((hesaplama, index) => (
                        <div key={index} className="relative pl-12">
                          <div className="absolute left-0 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-amber-300 shadow ring-4 ring-white">
                            {index + 1}
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md">
                            <div className="mb-3 flex items-center justify-between">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${hesaplama.isArttir ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                {hesaplama.isArttir ? <ArrowTrendingUpIcon className="h-3.5 w-3.5" /> : <ArrowTrendingDownIcon className="h-3.5 w-3.5" />}
                                {hesaplama.isArttir ? 'Artırım' : 'İndirim'}
                                <span className="opacity-50">·</span>
                                {hesaplama.oran.numerator}/{hesaplama.oran.denominator}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => handleYukariTasi(hesaplama.ceza, e)}
                                  title="Bu cezayı hesaplayıcıya yükle"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                >
                                  <ArrowUturnLeftIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => handleGecmisSil(index, e)}
                                  title="Sil"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                              <div className="min-w-0 sm:flex-1">
                                <p className="text-[11px] uppercase tracking-wider text-slate-400">Temel Ceza</p>
                                <p className="font-medium text-slate-600">
                                  {formatCezaWithSpaces(hesaplama.ceza)}
                                </p>
                                {hesaplama.ceza.fine > 0 && <p className="text-xs font-medium text-slate-500">{formatAPC(hesaplama.ceza.fine)}</p>}
                              </div>
                              <ArrowRightIcon className="h-5 w-5 shrink-0 rotate-90 self-center text-slate-300 sm:rotate-0 sm:self-auto" />
                              <div className="min-w-0 sm:flex-1 sm:text-right">
                                <p className="text-[11px] uppercase tracking-wider text-slate-400">Sonuç</p>
                                <p className="text-lg font-bold text-slate-900">
                                  {formatCezaWithSpaces(hesaplama.sonuc)}
                                </p>
                                {hesaplama.sonuc.fine > 0 && <p className="text-xs font-medium text-slate-500">{formatAPC(hesaplama.sonuc.fine)}</p>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculation;
